import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchSearchSuggestions } from "../api/searchApiService";
import { SearchSuggestion } from "../types/search.types";
import { sanitizeForUrl } from "../utils/urlSanitizer";

const DEBOUNCE_MS = 300;

export interface UseSearchReturn {
  searchQuery: string;
  suggestions: SearchSuggestion[];
  isSearching: boolean;
  showSuggestions: boolean;
  highlightedIndex: number;
  onQueryChange: (value: string) => void;
  onSuggestionClick: (suggestion: SearchSuggestion) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  closeSuggestions: () => void;
  clearSearch: () => void;
}

export const useSearch = (): UseSearchReturn => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const closeSuggestions = useCallback(() => {
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  }, []);

  const onQueryChange = useCallback((value: string) => {
    setSearchQuery(value);
    setHighlightedIndex(-1);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (!value.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    timeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      const results = await fetchSearchSuggestions(value);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
      setIsSearching(false);
    }, DEBOUNCE_MS);
  }, []);

  const onSuggestionClick = useCallback((suggestion: SearchSuggestion) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const anySugg = suggestion as any;
    const urlSlug = sanitizeForUrl(anySugg.productName ?? anySugg.name ?? "");
    const productCode = String(anySugg.code ?? anySugg.mappingCode ?? anySugg.productCode ?? "").trim();

    // If we have a product code, navigate to the product page (SPA navigation)
    if (productCode) {
      const productUrl = `/product/${encodeURIComponent(urlSlug)}/${encodeURIComponent(productCode)}`;
      clearSearch();
      navigate(productUrl);
      return;
    }

    // Fallback: open search results for the suggestion text so user can select the product
    const fallbackSearch = String(anySugg.productName ?? anySugg.name ?? "").trim();
    if (fallbackSearch) {
      clearSearch();
      navigate(`/search/product/${encodeURIComponent(fallbackSearch)}`);
    }
  }, [clearSearch, navigate]);

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        onSuggestionClick(suggestions[highlightedIndex]);
        return;
      }

      const trimmed = searchQuery.trim();
      if (!trimmed) return;

      clearSearch();
      navigate(`/search/product/${encodeURIComponent(trimmed)}`);
    },
    [highlightedIndex, suggestions, searchQuery, onSuggestionClick, clearSearch, navigate]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        closeSuggestions();
        return;
      }
      if (e.key === "Enter") {
        if (highlightedIndex >= 0 && suggestions.length > 0) {
          e.preventDefault();
          onSuggestionClick(suggestions[highlightedIndex]);
        } else {
          setShowSuggestions(false);
          setHighlightedIndex(-1);
        }
        return;
      }
      if (!showSuggestions || suggestions.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
      }
    },
    [highlightedIndex, suggestions, showSuggestions, closeSuggestions, onSuggestionClick]
  );

  return {
    searchQuery,
    suggestions,
    isSearching,
    showSuggestions,
    highlightedIndex,
    onQueryChange,
    onSuggestionClick,
    onKeyDown,
    onSubmit,
    closeSuggestions,
    clearSearch,
  };
};
